import { format, getDay, addDays, subDays } from 'date-fns';

// Funciones helper para colores de estado
export function getEstadoColor(estado) {
  switch (estado) {
    case 'Programada': return '#1cc88a';
    case 'Confirmada': return '#1cc88a';
    case 'Justificada': return '#e74a3b';
    case 'Realizada': return '#858796';
    case 'No asistió': return '#f6c23e';
    default: return '#5D1390';
  }
}

// Funciones para manejo de fechas
export function extractDateSafe(dateString) {
  if (!dateString) return '';
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  return dateString;
}

// ✅ FUNCIÓN CORREGIDA
export function extractTime(timeString) {
  if (!timeString) return '00:00:00';
  
  try {
    // Si viene en formato ISO completo (2025-01-20T16:00:00.000Z)
    if (timeString.includes('T')) {
      const timePart = timeString.split('T')[1];
      // Extraer solo HH:MM:SS sin conversión de zona horaria
      const cleanTime = timePart.replace('Z', '').split('.')[0];
      return cleanTime.substring(0, 8);
    }
    
    // Si ya es formato HH:MM:SS o HH:MM, retornar tal cual
    return timeString.substring(0, 8);
  } catch (error) {
    console.error('Error extracting time:', error);
    return '00:00:00';
  }
}

export function fixDateOffset(dateString) {
  if (!dateString) return '';
  
  try {
    if (dateString.includes('T') && dateString.includes('Z')) {
      const date = new Date(dateString);
      date.setDate(date.getDate() + 1);
      return format(date, 'yyyy-MM-dd');
    }
    
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    
    return dateString;
  } catch (error) {
    console.error('Error al corregir fecha:', error);
    return dateString;
  }
}

export function formatTime12h(timeString) {
  if (!timeString) return '';
  
  try {
    const [hoursStr, minutesStr] = timeString.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    
    if (isNaN(hours) || isNaN(minutes)) return timeString;
    
    let displayHours = hours;
    const period = hours >= 12 ? 'PM' : 'AM';
    
    if (hours === 0) {
      displayHours = 12;
    } else if (hours > 12) {
      displayHours = hours - 12;
    }
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return timeString;
  }
}

// Funciones para citas grupales
export function agruparCitasGrupales(eventos) {
  const eventosIndividuales = [];
  const eventosGrupalesMap = new Map();
  
  eventos.forEach(evento => {
    const esGrupal = evento.extendedProps?.grupo_id !== null && evento.extendedProps?.grupo_id !== undefined;
    
    if (!esGrupal) {
      eventosIndividuales.push(evento);
    } else {
      const grupoKey = `${evento.extendedProps.grupo_id}-${evento.fecha}-${evento.horaInicio}`;
      
      if (!eventosGrupalesMap.has(grupoKey)) {
        const pacientesGrupo = [evento.pacienteNombre].filter(Boolean);
        
        eventosGrupalesMap.set(grupoKey, {
          ...evento,
          id: `grupo-${evento.extendedProps.grupo_id}-${evento.fecha}-${evento.horaInicio.replace(':', '')}`,
          title: `Cita Grupal (1)`,
          color: '#2196F3',
          pacientesGrupo: pacientesGrupo,
          citasOriginales: [evento],
          totalPacientes: 1,
          esGrupalAgrupado: true,
          grupoNombre: evento.extendedProps?.grupoNombre || `Grupo ${evento.extendedProps.grupo_id}`
        });
      } else {
        const eventoAgrupado = eventosGrupalesMap.get(grupoKey);
        if (evento.pacienteNombre && !eventoAgrupado.pacientesGrupo.includes(evento.pacienteNombre)) {
          eventoAgrupado.pacientesGrupo.push(evento.pacienteNombre);
          eventoAgrupado.citasOriginales.push(evento);
          eventoAgrupado.totalPacientes = eventoAgrupado.pacientesGrupo.length;
          eventoAgrupado.title = `Cita Grupal (${eventoAgrupado.totalPacientes})`;
        }
      }
    }
  });
  
  const eventosGrupalesArray = Array.from(eventosGrupalesMap.values());
  return [...eventosIndividuales, ...eventosGrupalesArray];
}

export function esCitaGrupal(event) {
  return event.esGrupalAgrupado === true || (event.extendedProps?.grupo_id !== null && event.extendedProps?.grupo_id !== undefined);
}

export function formatTitleCitaGrupal(event, allEvents) {
  if (event.esGrupalAgrupado) {
    return event.title;
  }
  
  if (!esCitaGrupal(event)) {
    return event.pacienteNombre || event.title;
  }
  
  const grupoId = event.extendedProps.grupo_id;
  const mismoHorario = allEvents.filter(e => 
    e.fecha === event.fecha && 
    e.horaInicio === event.horaInicio &&
    e.extendedProps?.grupo_id === grupoId
  );
  
  const totalPacientes = mismoHorario.length;
  return `Cita Grupal (${totalPacientes})`;
}

export function getPacientesGrupoEnHorario(event, allEvents) {
  if (event.esGrupalAgrupado) {
    return event.pacientesGrupo || [];
  }
  
  if (!event.extendedProps?.grupo_id) return [];
  
  const grupoId = event.extendedProps.grupo_id;
  return allEvents.filter(e => 
    e.fecha === event.fecha && 
    e.horaInicio === event.horaInicio &&
    e.extendedProps?.grupo_id === grupoId
  ).map(e => e.pacienteNombre).filter(Boolean);
}

export function isDateInCurrentView(fecha, fechaActual, vista) {
  const fechaComparar = new Date(fecha);
  
  switch (vista) {
    case 'day':
      return format(fechaComparar, 'yyyy-MM-dd') === format(fechaActual, 'yyyy-MM-dd');
    case 'week':
      const day = getDay(fechaActual);
      const diff = day === 0 ? 6 : day - 1;
      const weekStart = subDays(fechaActual, diff);
      const weekEnd = addDays(weekStart, 6);
      return fechaComparar >= weekStart && fechaComparar <= weekEnd;
    case 'month':
      const monthStart = new Date(fechaActual.getFullYear(), fechaActual.getMonth(), 1);
      const monthEnd = new Date(fechaActual.getFullYear(), fechaActual.getMonth() + 1, 0);
      return fechaComparar >= monthStart && fechaComparar <= monthEnd;
    default:
      return false;
  }
}

export function updateUrlParams(searchParams, setSearchParams, updates) {
  const newParams = new URLSearchParams(searchParams);
  
  Object.entries(updates).forEach(([key, value]) => {
    if (value && value !== '') {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
  });
  
  setSearchParams(newParams, { replace: true });
}

export function getInitialStateFromUrl(searchParams) {
  const urlView = searchParams.get('view');
  const urlDate = searchParams.get('date');
  const urlEspecialista = searchParams.get('especialista');
  const urlEspecialidad = searchParams.get('especialidad');
  
  const today = new Date();
  let initialDate = today;
  let selectedDateValue = today;
  
  if (urlDate) {
    try {
      const parsedDate = new Date(urlDate);
      if (!isNaN(parsedDate.getTime())) {
        initialDate = parsedDate;
        selectedDateValue = parsedDate;
      }
    } catch (error) {
      console.warn('Fecha inválida en URL:', urlDate);
    }
  }
  
  return {
    currentView: ['month', 'week', 'day'].includes(urlView) ? urlView : 'month',
    currentDate: initialDate,
    selectedDate: selectedDateValue,
    selectedEspecialista: urlEspecialista || '',
    selectedEspecialidad: urlEspecialidad || ''
  };
}